import React from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";

function AddJobRequestModal({ show, onClose, defaultValues }) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch, // Add watch here
    formState: { errors },
  } = useForm({
    defaultValues: defaultValues || {}, // Set default values when editing
  });
  const navigate = useNavigate();
  const { token, setGlobalLoading } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [departments, setDepartments] = React.useState([]);
  const [subdepartments, setSubdepartments] = React.useState([]);
  const [filteredSubdepartments, setFilteredSubdepartments] = React.useState(
    []
  );
  const [levels, setLevels] = React.useState([]);
  const [worklocations, setWorklocations] = React.useState([]);
  const [users, setUsers] = React.useState([]);
  const [hradmins, setHradmins] = React.useState([]);
  const [mngt, setMngt] = React.useState([]);
  const [designations, setDesignations] = React.useState([]);
  const [jobDescription, setJobDescription] = React.useState({
    responsibility: "",
    skills_exp: "",
  });

  const selectedDepartmentId = watch("department");
  const selectedSubdepartmentId = watch("sub_department");
  const reqLapValue = watch("req_lap");
  const selectedDesignationId = watch("designation");

  // Fetch dropdown data (departments, subdepartments) when modal opens
  React.useEffect(() => {
    if (!show) return;

    const fetchDropdownData = async () => {
      try {
        setGlobalLoading(true);
        const res = await axios.get(`${API_BASE_URL}/employeeinfos/joblist`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setDepartments(res.data.departments || []);
        setSubdepartments(res.data.subdepartments || []);
        setLevels(res.data.levels || []);
        console.log(res.data.levels);
        setWorklocations(res.data.worklocations);
        setUsers(res.data.users);
        setHradmins(res.data.hradmins);
        setMngt(res.data.mngt);
        setFilteredSubdepartments([]);
        setDesignations([]);
      } catch (err) {
        console.error("Failed to load dropdown data:", err);
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchDropdownData();
  }, [show]);

  // Filter subdepartments based on department
  React.useEffect(() => {
    if (!selectedDepartmentId) {
      setFilteredSubdepartments([]);
      setValue("sub_department", "");
      setDesignations([]);
      setValue("designation", "");
      return;
    }

    const filtered = subdepartments.filter(
      (sub) => sub.parent_id === parseInt(selectedDepartmentId, 10)
    );

    setFilteredSubdepartments(filtered);
    setValue("sub_department", "");
    setDesignations([]);
    setValue("designation", "");
  }, [selectedDepartmentId, subdepartments]);

  // Fetch designations based on sub_department
  React.useEffect(() => {
    if (!selectedSubdepartmentId) {
      setDesignations([]);
      setValue("designation", "");
      return;
    }

    const fetchDesignations = async () => {
      try {
        setGlobalLoading(false);
        const res = await axios.get(
          `${API_BASE_URL}/employeeinfos/designations/${selectedSubdepartmentId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Fetched designations:", res.data);
        setDesignations(res.data || []);
        setValue("designation", "");
      } catch (err) {
        console.error("Failed to load designations:", err);
        setDesignations([]);
        setValue("designation", "");
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchDesignations();
  }, [selectedSubdepartmentId]);

  React.useEffect(() => {
    if (!selectedDesignationId) {
      setJobDescription({ responsibility: "", skills_exp: "" });
      return;
    }

    const fetchJobDescription = async () => {
      try {
        setGlobalLoading(true);
        const res = await axios.get(
          `${API_BASE_URL}/employeeinfos/jobdescrition/${selectedDesignationId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.data && res.data.jobDescription.length > 0) {
          const { resonsibilty, skills_exp } = res.data.jobDescription[0];
          setJobDescription({ resonsibilty, skills_exp });
          setValue("resonsibilty", resonsibilty); // Populate responsibility field
          setValue("skills_exp", skills_exp); // Populate key skills field
        }
      } catch (err) {
        console.error("Failed to fetch job description:", err);
      } finally {
        setGlobalLoading(false);
      }
    };

    fetchJobDescription();
  }, [selectedDesignationId]);

  const handleReqLapChange = (e) => {
    const value = e.target.value;
    setValue("req_lap", value);

    // If "No" is selected, clear the special_requirement_laptop field
    if (value == 0) {
      setValue("special_requirement_laptop", "");
    }
  };

  const onSubmit = async (data) => {
    try {
      setGlobalLoading(true);
      if (defaultValues && defaultValues.id) {
        // Update existing job request
        await axios.put(
          `${API_BASE_URL}/employeeinfos/editJobRequest/${defaultValues.id}`,
          data,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert("Job request updated successfully");
      } else {
        // Add new job request
        await axios.post(`${API_BASE_URL}/employeeinfos/addJobRequest`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert("Job request added successfully");
      }

      reset(); // Reset the form
      onClose(); // Close the modal
    } catch (err) {
      console.error("Submission failed:", err);
      alert("Failed to submit job request");
    } finally {
      setGlobalLoading(false);
      setTimeout(() => {
        navigate("/employeeinfo/joblist");
      }, 1000);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "800px" }}
      >
        <div className="modal-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h5 className="modal-title">
                {defaultValues ? "Edit Job Request" : "Add Job Request"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>

            <div className="modal-body">
              {/* Role Type */}
              <div className="mb-3">
                <label className="form-label">Role Type</label>
                <select
                  className={`form-select ${
                    errors.roletype ? "is-invalid" : ""
                  }`}
                  {...register("role_type", { required: "Required" })}
                >
                  <option value="">-- Select --</option>
                  <option value="Permanent">Permanent</option>
                  <option value="Intern">Intern</option>
                  <option value="Contract">Contract Basics</option>
                </select>
                {errors.roletype && (
                  <div className="invalid-feedback">
                    {errors.roletype.message}
                  </div>
                )}
              </div>
              {/* <div className="mb-3">
                <label className="form-label">Type of Hiring</label>
                <select
                  className={`form-select ${
                    errors.hiringtype ? "is-invalid" : ""
                  }`}
                  {...register("hiringtype", { required: "Required" })}
                >
                  <option value="">-- Select --</option>
                  <option value="Permanent">Campus</option>
                  <option value="Intern">Lateral</option>
                </select>
                {errors.roletype && (
                  <div className="invalid-feedback">
                    {errors.roletype.message}
                  </div>
                )}
              </div> */}
              {/* Department */}
              <div className="mb-3">
                <label className="form-label">Department</label>
                <select
                  className={`form-select ${
                    errors.department ? "is-invalid" : ""
                  }`}
                  {...register("department", { required: "Required" })}
                >
                  <option value="">-- Select Department --</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {errors.department && (
                  <div className="invalid-feedback">
                    {errors.department.message}
                  </div>
                )}
              </div>

              {/* Sub Department */}
              <div className="mb-3">
                <label className="form-label">Sub Department</label>
                <select
                  className={`form-select ${
                    errors.sub_department ? "is-invalid" : ""
                  }`}
                  {...register("sub_department", { required: "Required" })}
                  disabled={!filteredSubdepartments.length}
                >
                  <option value="">-- Select Sub Department --</option>
                  {filteredSubdepartments.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </select>
                {errors.sub_department && (
                  <div className="invalid-feedback">
                    {errors.sub_department.message}
                  </div>
                )}
              </div>

              {/* Designation */}
              <div className="mb-3">
                <label className="form-label">Designation</label>
                <select
                  className={`form-select ${
                    errors.designation ? "is-invalid" : ""
                  }`}
                  {...register("designation", { required: "Required" })}
                  disabled={!designations.length}
                >
                  <option value="">-- Select Designation --</option>
                  {designations.map((des) => (
                    <option key={des.id} value={des.id}>
                      {des.name}
                    </option>
                  ))}
                </select>
                {errors.designation && (
                  <div className="invalid-feedback">
                    {errors.designation.message}
                  </div>
                )}
              </div>

              {/* No. of Positions */}
              <div className="mb-3">
                <label className="form-label">No. of Positions</label>
                <input
                  type="number"
                  className="form-control"
                  {...register("no_req", { required: "Required" })}
                />
                {errors.no_req && (
                  <div className="invalid-feedback">
                    {errors.no_req.message}
                  </div>
                )}
              </div>

              {/* Work Location */}
              <div className="mb-3">
                <label className="form-label">Work Location</label>
                <select
                  className={`form-select ${
                    errors.worklocation ? "is-invalid" : ""
                  }`}
                  {...register("worklocation", { required: "Required" })}
                >
                  <option value="">-- Select Work Location --</option>
                  {worklocations.map((loc) => (
                    <option key={loc.id} value={loc.name}>
                      {loc.name}
                    </option>
                  ))}
                </select>
                {errors.worklocation && (
                  <div className="invalid-feedback">
                    {errors.worklocation.message}
                  </div>
                )}
              </div>

              {/* Role Responsibilities */}
              <div className="mb-3">
                <label className="form-label">Role Responsibilities *</label>
                <textarea
                  rows="4"
                  className="form-control"
                  placeholder="Enter job description"
                  {...register("responsibility", {
                    required: "Role responsibilities are required",
                  })}
                  value={jobDescription.resonsibilty} // Populating responsibility from state
                ></textarea>
                {errors.responsibility && (
                  <div className="invalid-feedback">
                    {errors.responsibility.message}
                  </div>
                )}
              </div>

              {/* Role Key Skills */}
              <div className="mb-3">
                <label className="form-label">Role Key Skills *</label>
                <textarea
                  rows="4"
                  className="form-control"
                  placeholder="Enter key skills"
                  {...register("skills_exp", {
                    required: "Key skills are required",
                  })}
                  value={jobDescription.skills_exp} // Populating skills from state
                ></textarea>
                {errors.skills_exp && (
                  <div className="invalid-feedback">
                    {errors.skills_exp.message}
                  </div>
                )}
              </div>

              {/* Seniority Level */}
              <div className="mb-3">
                <label className="form-label">Seniority Level</label>
                <div>
                  {levels.map((level) => (
                    <div
                      className="form-check form-check-inline"
                      key={level.id}
                    >
                      <input
                        className="form-check-input"
                        type="radio"
                        value={level.id}
                        {...register("levels", { required: "Required" })}
                      />
                      <label className="form-check-label">{level.name}</label>
                    </div>
                  ))}
                </div>
                {errors.seniority && (
                  <div className="text-danger">{errors.seniority.message}</div>
                )}
              </div>

              {/* Reports To */}
              <div className="mb-3">
                <label className="form-label">Reports To *</label>
                <select
                  className={`form-select ${errors.users ? "is-invalid" : ""}`}
                  {...register("reports_to", { required: "Required" })}
                >
                  <option value="">-- Select Reporting Manager --</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
                {errors.users && (
                  <div className="invalid-feedback">{errors.users.message}</div>
                )}
              </div>

              {/* Assigned to */}
              <div className="mb-3">
                <label className="form-label">Assigned to *</label>
                <select
                  className={`form-select ${
                    errors.hradmins ? "is-invalid" : ""
                  }`}
                  {...register("assigned_to", { required: "Required" })}
                >
                  <option value="">-- Select Reporting Manager --</option>
                  {hradmins.map((hradmin) => (
                    <option key={hradmin.id} value={hradmin.id}>
                      {hradmin.name}
                    </option>
                  ))}
                </select>
                {errors.hradmins && (
                  <div className="invalid-feedback">
                    {errors.hradmins.message}
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label">Approved By*</label>
                <select
                  className={`form-select ${errors.mngt ? "is-invalid" : ""}`}
                  {...register("approved_by", { required: "Required" })}
                >
                  <option value="">-- Select Reporting Manager --</option>
                  {mngt.map((mngt) => (
                    <option key={mngt.id} value={mngt.id}>
                      {mngt.name}
                    </option>
                  ))}
                </select>
                {errors.mngt && (
                  <div className="invalid-feedback">{errors.mngt.message}</div>
                )}
              </div>

              {/* Special Laptop Requirement */}
              <div className="mb-3">
                <label className="form-label">Laptop required</label>
                <div>
                  {Object.entries({ 1: "Yes", 0: "No" }).map(
                    ([value, label]) => (
                      <div className="form-check form-check-inline" key={value}>
                        <input
                          className="form-check-input"
                          type="radio"
                          value={value}
                          {...register("laptop")}
                          onChange={handleReqLapChange}
                        />
                        <label className="form-check-label">{label}</label>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Conditionally show the Special Requirement Laptop field */}
              {reqLapValue == 1 && (
                <div className="mb-3">
                  <label className="form-label">
                    Special Requirement Laptop *
                  </label>
                  <textarea
                    rows="4"
                    className="form-control"
                    placeholder="Enter special laptop requirements"
                    {...register("lap_req", {
                      required: "Required",
                    })}
                  ></textarea>
                  {errors.special_requirement_laptop && (
                    <div className="invalid-feedback">
                      {errors.special_requirement_laptop.message}
                    </div>
                  )}
                </div>
              )}

              {/* Approval Screenshot */}
              {/* <div className="mb-3">
                <label className="form-label">Approval Screenshot *</label>
                <input
                  type="file"
                  className="form-control"
                  {...register("jd_file", { required: "Required" })}
                />
                {errors.jd_file && (
                  <div className="invalid-feedback">
                    {errors.jd_file.message}
                  </div>
                )}
              </div> */}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-success">
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddJobRequestModal;
